import type { IDropdownMenuItem, IMenuItem } from './types';
import { getRoutePath } from '@/routes/util';
import i18n from '@/shared/i18n';
import type { IFeatures } from '@/shared/types/features';

export const DOCUMENTATION_URL = 'https://docs.kognitos.com';

// Helper function to check if a URL is a workspace item
export const isWorkspaceItem = (pathname: string): boolean => {
  const workspaceUrls = getWorkspaceMenuItems().flatMap(item => {
    if (item.url) {
      return [item.url];
    }
    if (item.children) {
      return item.children.map(child => child.url!).filter(Boolean);
    }
    return [];
  });

  return workspaceUrls.some(
    url => pathname === url || pathname.startsWith(url + '/'),
  );
};

// Helper function to check if a URL is an organization item
export const isOrganizationItem = (pathname: string): boolean => {
  const organizationUrls = getOrganizationMenuItems().flatMap(item => {
    if (item.url) {
      return [item.url];
    }
    return [];
  });

  return organizationUrls.some(
    url => pathname === url || pathname.startsWith(url + '/'),
  );
};

// Workspace menu items
export const getWorkspaceMenuItems = (
  orgId: string = '',
  workspaceId: string = '',
): IMenuItem[] => [
  // {
  //   key: 'chats',
  //   title: i18n.t('chat.title', { ns: 'chat' }),
  //   url: getRoutePath('CHAT', { orgId, workspaceId }),
  //   icon: 'Sparkles',
  //   tooltip: i18n.t('chat.title', { ns: 'chat' }),
  // },
  {
    key: 'drafts',
    title: i18n.t('workspace.drafts.title', { ns: 'workspace' }),
    url: getRoutePath('DRAFTS', { orgId, workspaceId }),
    icon: 'PenLine',
    tooltip: i18n.t('workspace.drafts.title', { ns: 'workspace' }),
  },
  {
    key: 'automations',
    title: i18n.t('automations.title', { ns: 'automations' }),
    url: getRoutePath('AUTOMATIONS', { orgId, workspaceId }),
    icon: 'Text',
    tooltip: i18n.t('automations.title', { ns: 'automations' }),
  },
  // {
  //   key: 'learnings',
  //   title: i18n.t('workspace.learnings.title', { ns: 'workspace' }),
  //   url: getRoutePath('LEARNINGS', { orgId, workspaceId }),
  //   icon: 'Sparkles',
  //   tooltip: i18n.t('workspace.learnings.title', { ns: 'workspace' }),
  // },
  {
    key: 'guidance-center',
    title: i18n.t('guidance.center.title', { ns: 'guidance' }),
    url: getRoutePath('GUIDANCE_CENTER', { orgId, workspaceId }),
    icon: 'Compass',
    tooltip: i18n.t('guidance.center.title', { ns: 'guidance' }),
  },
  {
    key: 'troubleshooting-guides',
    title: i18n.t('guidance.troubleshooting.title', { ns: 'guidance' }),
    url: getRoutePath('TROUBLESHOOTING_GUIDE', { orgId, workspaceId }),
    icon: 'Wrench',
    tooltip: i18n.t('guidance.troubleshooting.title', { ns: 'guidance' }),
  },
  {
    key: 'integrations',
    title: i18n.t('integrations.title', { ns: 'integrations' }),
    url: getRoutePath('INTEGRATIONS', { orgId, workspaceId }),
    icon: 'Blocks',
    tooltip: i18n.t('integrations.title', { ns: 'integrations' }),
  },
];

// For backward compatibility
export const WORKSPACE_MENU_ITEMS = getWorkspaceMenuItems();

// Organization items
export const getOrganizationMenuItems = (
  orgId: string = '',
): IDropdownMenuItem[] => [
  {
    key: 'performance',
    title: i18n.t('organization.performance.title', { ns: 'organization' }),
    icon: 'LineChart',
    url: getRoutePath('ORGANIZATION_PERFORMANCE', { orgId }),
  },
];

// User dropdown menu items
export const getUserMenuItems = ({
  currentOrganizationId,
  currentWorkspaceId,
  onSelect,
  logout,
  features = {},
}: {
  currentOrganizationId: string | null;
  currentWorkspaceId: string | null;
  onSelect?: () => void;
  logout: () => void;
  features?: IFeatures;
}): IDropdownMenuItem[] => {
  const { enableBifrostUI } = features;
  return [
    {
      key: 'performance',
      title: i18n.t('organization.performance.title', { ns: 'organization' }),
      icon: 'LineChart',
      url: currentOrganizationId
        ? getRoutePath('ORGANIZATION_PERFORMANCE', {
            orgId: currentOrganizationId,
          })
        : undefined,
      disabled: !currentOrganizationId,
      onClick: onSelect,
    },
    // {
    //   key: 'separator-1',
    //   type: 'separator',
    // },
    // {
    //   key: 'tutorials',
    //   title: i18n.t('resources.tutorials.title', { ns: 'resources' }),
    //   icon: 'TvMinimalPlay',
    //   url: getRoutePath('TUTORIALS'),
    //   onClick: onSelect,
    // },
    {
      key: 'documentation',
      title: i18n.t('navigation.documentation', { ns: 'navigation' }),
      icon: 'BookOpen',
      isExternalUrl: true,
      url: DOCUMENTATION_URL,
      onClick: onSelect,
    },
    {
      key: 'separator-2',
      type: 'separator',
    },
    {
      key: 'workspace-settings',
      title: i18n.t('settings.titles.workspaceSettings', { ns: 'settings' }),
      icon: 'Settings2',
      url:
        currentWorkspaceId && currentOrganizationId
          ? getRoutePath('WORKSPACE_SETTINGS', {
              orgId: currentOrganizationId,
              workspaceId: currentWorkspaceId,
            })
          : undefined,
      disabled: !currentWorkspaceId || !currentOrganizationId,
      onClick: onSelect,
    },
    {
      key: 'organization-settings',
      title: i18n.t('navigation.organizationSettings', { ns: 'navigation' }),
      icon: 'UserCog',
      url: currentOrganizationId
        ? getRoutePath('ORG_SETTINGS', { orgId: currentOrganizationId })
        : undefined,
      disabled: !currentOrganizationId,
      onClick: onSelect,
    },
    ...(enableBifrostUI && currentOrganizationId
      ? [
          {
            key: 'remote-connections',
            title: i18n.t('navigation.remoteConnections', { ns: 'navigation' }),
            icon: 'GlobeLock' as const,
            url: getRoutePath('REMOTE_CONNECTIONS', {
              orgId: currentOrganizationId,
            }),
            onClick: onSelect,
          },
        ]
      : []),
    // {
    //   key: 'api-keys',
    //   title: i18n.t('settings.apiKeys.title', { ns: 'settings' }),
    //   icon: 'Key',
    //   url: getRoutePath('API_KEYS'),
    //   onClick: onSelect,
    // },
    // {
    //   key: 'preferences',
    //   title: i18n.t('settings.preferences.title', { ns: 'settings' }),
    //   icon: 'Settings2',
    //   url: getRoutePath('PREFERENCES'),
    //   onClick: onSelect,
    // },
    {
      key: 'separator-3',
      type: 'separator',
    },
    {
      key: 'profile',
      title: i18n.t('navigation.profile', { ns: 'navigation' }),
      icon: 'UserRound',
      url: getRoutePath('USER_SETTINGS'),
      onClick: onSelect,
    },
    {
      key: 'logout',
      title: i18n.t('navigation.logOut', { ns: 'navigation' }),
      icon: 'LogOut',
      onClick: () => {
        logout();
        onSelect?.();
      },
    },
    {
      key: 'separator-4',
      type: 'separator',
    },
    {
      key: 'switch-organization',
      title: i18n.t('navigation.switchOrganization', { ns: 'navigation' }),
      shortcut: 'mod+shift+o',
      onClick: onSelect,
    },
  ];
};
